//Todo move this state to admin folder after refactoring
import { Injectable } from '@angular/core';
import {
  Action,
  createSelector,
  Selector,
  State,
  StateContext,
} from '@ngxs/store';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import {
  DashboardControllerService,
  DashboardWithRelations,
  FilterExpression,
  StatisticQuery,
} from '@osos/shared/api';
import {
  DashboardEntryPresenterModel,
  DashboardScope,
  FilterExpressionModel,
  LoadingIndicator,
  LoadingType,
  ManagedHttpContext,
  StatisticQueryPresenterModel,
  StatisticQueryResultItemModel,
  StatisticQueryResultModel,
  Where,
} from '@osos/shared/types';
import { Observable, of } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { StatisticQueryFactoryService } from '../../factories';
import { DashboardFactoryService } from '../../factories/dashboard-factory/dashboard-factory.service';
import { ApiUtilsService } from '../../services';
import { StatisticQueryDataService } from '../../services/data-services/statistic-query-data-service/statistic-query-data.service';
import { LoadingActions } from '../loading/loading.actions';
import { DashboardActions } from './dashboard.action';

export interface DashboardStateModel {
  presentedDashboard: { [key: string]: DashboardEntryPresenterModel };
  presentedDashboards?: DashboardEntryPresenterModel[];
  selectedDashboard?: string;
  transitoryDashboard?: DashboardEntryPresenterModel[];
  statisticQueryResult?: {
    [key: string]: StatisticQueryResultModel;
  };
  presentedStatisticQuery: { [key: string]: StatisticQueryPresenterModel };
  presentedStatisticQueries: StatisticQueryPresenterModel[];
  loading: LoadingIndicator;
}

@State<DashboardStateModel>({
  name: 'Dashboard',
  defaults: {
    presentedDashboard: {},
    presentedDashboards: [],
    transitoryDashboard: [],
    statisticQueryResult: {},
    presentedStatisticQuery: {},
    presentedStatisticQueries: [],
    loading: new LoadingIndicator(),
  },
})
@Injectable()
export class DashboardState {
  constructor(
    private dashboardFactory: DashboardFactoryService,
    private apiHelper: ApiUtilsService,
    private dashboardControllerService: DashboardControllerService,
    private statisticQueriesService: StatisticQueryDataService,
    private statisticQueryFactoryService: StatisticQueryFactoryService,
  ) {}

  /** Selectors **/
  static transitoryDashboards(
    state: DashboardStateModel,
  ): DashboardEntryPresenterModel[] {
    return state?.transitoryDashboard;
  }

  @Selector([DashboardState])
  static selectedDashboard(
    state: DashboardStateModel,
  ): DashboardEntryPresenterModel {
    return state.presentedDashboard[state.selectedDashboard];
  }

  static presentedDashboard(
    id: string,
  ): (state: DashboardStateModel) => DashboardEntryPresenterModel {
    return createSelector(
      [DashboardState],
      (state: DashboardStateModel) => state.presentedDashboard[id],
    );
  }

  private getKey = DashboardState.getKey;

  private static getKey(entityId: string, queryId: string): string {
    return `${entityId ?? ''}${queryId}`;
  }

  // --------------------------------- SELECTORS ------------------------------

  static statisticQueryResult(
    entityId,
    queryId,
  ): (state: DashboardStateModel) => StatisticQueryResultModel {
    return createSelector(
      [DashboardState],
      (state: DashboardStateModel) =>
        state.statisticQueryResult[this.getKey(entityId, queryId)],
    );
  }

  static presentedStatisticQuery(
    id: string,
  ): (state: DashboardStateModel) => StatisticQueryPresenterModel {
    return createSelector([DashboardState], (state: DashboardStateModel) => {
      return state.presentedStatisticQuery[id];
    });
  }

  @Selector([DashboardState])
  static presentedStatisticQueries(
    state: DashboardStateModel,
  ): StatisticQueryPresenterModel[] {
    return state.presentedStatisticQueries;
  }

  static loading(type: LoadingType): (state: DashboardStateModel) => boolean {
    return createSelector(
      [DashboardState],
      (state: DashboardStateModel) => state.loading[type],
    );
  }

  /** Actions **/
  @Action(DashboardActions.FetchAndSelectDashboard)
  FetchAndSelectDashboard(
    { setState }: StateContext<DashboardStateModel>,
    { moduleId, entityId, viewId }: DashboardActions.FetchAndSelectDashboard,
  ) {
    const where: Where<DashboardWithRelations> = this.getDashboardFilter(
      moduleId,
      entityId,
    ) as Where<DashboardWithRelations>;
    return this.dashboardControllerService
      .find({
        ...this.apiHelper.prepareFilter<DashboardWithRelations>({
          where,
        }),
        context: viewId
          ? new ManagedHttpContext({
              publicAccessId: viewId,
            })
          : undefined,
      })
      .pipe(
        tap((dashboards) => {
          if (!dashboards.length) {
            setState(
              patch<DashboardStateModel>({
                selectedDashboard: null,
              }),
            );
          } else {
            const presenter = this.dashboardFactory.createPresenterModel(
              dashboards[0],
            );
            setState(
              patch<DashboardStateModel>({
                presentedDashboard: patch({
                  [presenter.id]: presenter,
                }),
                selectedDashboard: presenter.id,
              }),
            );
          }
        }),
      );
  }

  @Action(DashboardActions.updateDashboard)
  updateDashboard(
    { setState, dispatch }: StateContext<DashboardStateModel>,
    { payload }: DashboardActions.updateDashboard,
  ) {
    dispatch(new LoadingActions.SetCommandLoading(true));

    const dashboard = payload.dashboard;

    return this.dashboardControllerService
      .updateById({
        id: dashboard.id,
        body: {
          value: this.dashboardFactory.createDataModel(dashboard).value,
        },
      })
      .pipe(
        switchMap(() => {
          const where: Where<DashboardWithRelations> = {
            id: dashboard.id,
          };
          return this.dashboardControllerService.find(
            this.apiHelper.prepareFilter<DashboardWithRelations>({
              where,
            }),
          );
        }),
        tap((dashboardModel) => {
          if (dashboardModel.length) {
            const presenter = this.dashboardFactory.createPresenterModel(
              dashboardModel[0],
            );
            setState(
              patch<DashboardStateModel>({
                presentedDashboard: patch({
                  [presenter.id]: presenter,
                }),
                selectedDashboard: presenter.id,
              }),
            );
          }
        }),
        finalize(() => dispatch(new LoadingActions.SetCommandLoading(false))),
      );
  }

  @Action(DashboardActions.TransitoryExecuteStatisticQuery)
  transitoryExecuteStatisticQuery(
    { setState, getState }: StateContext<DashboardStateModel>,
    {
      entityId,
      queryId,
      filterField,
      startDate,
      toDate,
      forceRefresh,
    }: DashboardActions.TransitoryExecuteStatisticQuery,
  ): Observable<StatisticQueryResultModel> {
    const cachedExecutedResult =
      getState().statisticQueryResult[this.getKey(entityId, queryId)];
    if (cachedExecutedResult && !filterField && !forceRefresh) {
      return of(cachedExecutedResult);
    }

    const where =
      !!filterField && !!startDate && !!toDate
        ? this.apiHelper.prepareWhere({
            and: [
              { [`${filterField}`]: { gte: startDate } },
              { [`${filterField}`]: { lte: toDate } },
            ],
          })
        : undefined;

    return this.statisticQueriesService
      .executeStatisticQueryV2({
        entity: entityId,
        id: queryId,
        where,
      })
      .pipe(
        map((res) => this._getStaticQueryResultModel(res)),
        tap((res) =>
          setState(
            patch<DashboardStateModel>({
              statisticQueryResult: patch({
                [this.getKey(entityId, queryId)]: res,
              }),
            }),
          ),
        ),
      );
  }

  @Action(DashboardActions.TransitoryFetchStatisticQuery)
  transitoryFetchStatisticQuery(
    { setState, getState }: StateContext<DashboardStateModel>,
    { queryId }: DashboardActions.TransitoryFetchStatisticQuery,
  ): Observable<any> {
    const query = getState().presentedStatisticQuery[queryId];
    if (query) {
      return of(query);
    }

    return this.statisticQueriesService
      .findByIdWithoutEntity({
        id: queryId,
      })
      .pipe(
        tap((query) => {
          setState(
            patch<DashboardStateModel>({
              presentedStatisticQuery: patch({
                [queryId]:
                  this.statisticQueryFactoryService.createPresenterModel(query),
              }),
            }),
          );
        }),
      );
  }

  @Action(DashboardActions.FetchStatisticQueries, {
    cancelUncompleted: true,
  })
  fetchStatisticQueries(
    { setState, patchState }: StateContext<DashboardStateModel>,
    { payload, skip, limit }: DashboardActions.FetchStatisticQueries,
  ): Observable<any> {
    setState(
      patch<DashboardStateModel>({
        presentedStatisticQueries: [],
        loading: patch<LoadingIndicator>({
          page: true,
        }),
      }),
    );

    return this.statisticQueriesService
      .find({
        entity: payload,
        filter: this.apiHelper.prepareFilter({
          skip,
          limit,
        }),
      })
      .pipe(
        tap((items: StatisticQuery[]) => {
          patchState({
            presentedStatisticQueries: items.map((query) =>
              this.statisticQueryFactoryService.createPresenterModel(query),
            ),
          });
        }),
        finalize(() => {
          setState(
            patch<DashboardStateModel>({
              loading: patch<LoadingIndicator>({
                page: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(DashboardActions.CreateStatisticQuery)
  createStatisticQuery(
    { setState }: StateContext<DashboardStateModel>,
    { payload }: DashboardActions.CreateStatisticQuery,
  ) {
    const presenter =
      this.statisticQueryFactoryService.createPresenterModel(payload);
    setState(
      patch<DashboardStateModel>({
        presentedStatisticQueries: append([presenter]),
      }),
    );
  }

  @Action(DashboardActions.UpdateStatisticQuery)
  updateStatisticQuery(
    { setState }: StateContext<DashboardStateModel>,
    { payload }: DashboardActions.UpdateStatisticQuery,
  ) {
    setState(
      patch<DashboardStateModel>({
        presentedStatisticQueries: updateItem(
          (i) => i.id === payload.id,
          payload,
        ),
        presentedStatisticQuery: patch({
          [payload.id]: null,
        }),
        statisticQueryResult: patch({
          [this.getKey(payload.dataEntity, payload.id)]: null,
        }),
      }),
    );
  }

  @Action(DashboardActions.DeleteStatisticQuery)
  deleteStatisticQuery(
    ctx: StateContext<DashboardStateModel>,
    { payload }: DashboardActions.DeleteStatisticQuery,
  ) {
    ctx.setState(
      patch<DashboardStateModel>({
        presentedStatisticQueries: removeItem((i) => i.id === payload.id),
      }),
    );
  }

  @Action(DashboardActions.ClearPresentedStatistic)
  clearPresentedStatistic({ setState }: StateContext<DashboardStateModel>) {
    setState(
      patch<DashboardStateModel>({
        presentedStatisticQuery: {},
        statisticQueryResult: {},
      }),
    );
  }

  @Action(DashboardActions.ClearPresentedStatisticQueryByQuerySource)
  clearPresentedStatisticQueryByQuerySource(
    { setState, getState }: StateContext<DashboardStateModel>,
    {
      dataEntityId,
      dataQueryId,
    }: DashboardActions.ClearPresentedStatisticQueryByQuerySource,
  ) {
    const presentedStatisticQuery = getState().presentedStatisticQuery;
    const statisticQueries = dataQueryId
      ? Object.keys(presentedStatisticQuery).filter(
          (q) => presentedStatisticQuery[q].source.query == dataQueryId,
        )
      : [];

    // TODO replace with UpdateManyItems after updating it to accept object
    statisticQueries.forEach((st) => {
      setState(
        patch<DashboardStateModel>({
          presentedStatisticQuery: patch({
            [st]: null,
          }),
          statisticQueryResult: patch({
            [this.getKey(dataEntityId, st)]: null,
          }),
        }),
      );
    });
  }

  @Action(DashboardActions.ClearPresentedStatisticByEntityId)
  ClearPresentedStatisticByEntityId(
    { setState, getState }: StateContext<DashboardStateModel>,
    { dataEntityId }: DashboardActions.ClearPresentedStatisticByEntityId,
  ): void {
    const presentedStatisticQuery = getState().presentedStatisticQuery;
    const statisticQueries = dataEntityId
      ? Object.keys(presentedStatisticQuery).filter(
          (q) => presentedStatisticQuery[q]?.dataEntity === dataEntityId,
        )
      : [];

    // TODO replace with UpdateManyItems after updating it to accept object
    statisticQueries.forEach((st) => {
      setState(
        patch<DashboardStateModel>({
          statisticQueryResult: patch({
            [this.getKey(dataEntityId, st)]: null,
          }),
        }),
      );
    });
  }

  private getDashboardFilter(moduleId?: string, entityId?: string) {
    if (entityId) {
      return this.getEntityDashboardFilter(entityId);
    } else if (moduleId) {
      return this.getModuleDashboardFilter(moduleId);
    } else {
      return this.getSystemDashboardFilter();
    }
  }

  private getEntityDashboardFilter(entityId: string) {
    return {
      ['value.relatedTo.dataEntry']: entityId,
      ['value.relatedTo.dataEntity']: DashboardScope.DataEntity,
    };
  }

  private getModuleDashboardFilter(moduleId: string) {
    return {
      ['value.relatedTo.dataEntry']: { eq: moduleId },
      ['value.relatedTo.dataEntity']: { eq: DashboardScope.DataModule },
    };
  }

  private getSystemDashboardFilter() {
    return {
      ['value.relatedTo.dataEntity']: { exists: false },
    };
  }

  private _getStaticQueryResultModel(stat: {
    result?: Array<StatisticQueryResultItemModel>;
    count?: number;
    query?: FilterExpression;
    hash?: string;
  }): StatisticQueryResultModel {
    return {
      query: (stat?.query ? stat.query : null) as FilterExpressionModel,
      result: stat?.result?.length ? stat.result : [],
      count: stat?.count ? stat.count : 0,
      hash: stat?.hash,
    };
  }
}
